import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Result,
  Row,
  Select,
  Space,
  Typography,
  Input,
  Upload
} from 'antd';
import { DOCTOR_STATUSES } from 'constants/doctors';
import { useNavigate } from 'react-router-dom';
import { fetchTranslatorPlans } from 'services/api/translatorPlans';
import { TranslatorPlan } from 'types/api/translatorPlans';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useFileUpload } from 'hooks';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { URL_VALIDATION } from '@medicusunion/front-utils';
import { createAppointment } from 'services/api/appointments';
import { fetchDoctorById, fetchDoctors } from 'services/api/doctors';
import { DoctorListRow, DoctorProfile } from 'types/api/doctors';

import { PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';
import { pickLocale } from 'utils/pickLocale';
import { validateLink } from 'utils/link';
import { PatientSelect } from './components';

const { Text, Link } = Typography;
const { TextArea } = Input;

type FieldValues = {
  link?: string;
};

const CreateAppointment: FC = () => {
  // translations
  const { t } = useTranslation();
  // navigation
  const navigate = useNavigate();
  // Form
  const [form] = Form.useForm();
  // states
  const [doctorID, setDoctorId] = useState('');
  const [links, setLinks] = useState<string[]>([]);
  const [files, setFiles] = useState<string[]>([]);
  //refs
  const uploadQueue = useRef<UploadRequestOption[]>([]);
  const uploadQueueTimer = useRef<NodeJS.Timeout | null>(null);
  //AWS
  const { upload } = useFileUpload({
    handleResult: true,
    loading: true
  });

  const [doctorsData, setDoctorsData] = useState<DoctorListRow[]>([]);
  const [doctorsLoading, setDoctorsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setDoctorsLoading(true);
        const res = await fetchDoctors({
          page: 1,
          limit: 999,
          status: DOCTOR_STATUSES.active
        });

        if (!cancelled) setDoctorsData(res.results);
      } catch {
        // swallow — empty picker on failure
      } finally {
        if (!cancelled) setDoctorsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const [translatorPlansData, setTranslatorPlansData] = useState<
    TranslatorPlan[]
  >([]);

  const [translatorPlansLoading, setTranslatorPlansLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setTranslatorPlansLoading(true);
        const res = await fetchTranslatorPlans({ page: 1, limit: 999 });
        if (!cancelled) setTranslatorPlansData(res.results);
      } catch {
        // swallow — empty picker on failure
      } finally {
        if (!cancelled) setTranslatorPlansLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const [doctorProfile, setDoctorProfile] = useState<DoctorProfile | null>(
    null
  );

  const [doctorProfileLoading, setDoctorProfileLoading] = useState(false);

  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    if (!doctorID) {
      setDoctorProfile(null);

      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setDoctorProfileLoading(true);
        const profile = await fetchDoctorById(doctorID, 'en');
        if (!cancelled) setDoctorProfile(profile);
      } catch {
        if (!cancelled) setDoctorProfile(null);
      } finally {
        if (!cancelled) setDoctorProfileLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [doctorID]);

  const onAddLinkClick = useCallback(async () => {
    try {
      const field = await form.validateFields(['link']);

      if (field.link) {
        const link = form.getFieldValue('link');

        form.setFieldsValue({
          link: ''
        });
        setLinks(prev => [...prev, link]);
      }
    } catch (_) {
      //
    }
  }, [form]);

  const doctors = doctorsData.map(item => ({
    label: `${pickLocale(item.first_name, 'en')} ${pickLocale(
      item.last_name,
      'en'
    )} (${item.email})`,
    value: String(item.id)
  }));

  const doctorPlans = doctorProfile?.plans?.map(item => ({
    label: item.name,
    value: item.id
  }));

  const translatorPlans = translatorPlansData.map(item => ({
    label: item.name,
    value: item.id
  }));

  useEffect(() => {
    if (!doctorPlans?.length) {
      form.resetFields(['plan_id']);
    }
  }, [doctorPlans?.length, form]);

  const onUploadFile = (e: UploadRequestOption) => {
    uploadQueue.current.push(e);

    if (uploadQueueTimer.current) {
      clearTimeout(uploadQueueTimer.current);
    }

    uploadQueueTimer.current = setTimeout(async () => {
      for (let i = 0; i < uploadQueue.current.length; i++) {
        const item = uploadQueue.current[i];

        try {
          const res = await upload(item.file as File);

          if (res) {
            setFiles(prev => [...prev, res]);
          }

          item.onSuccess?.(res);
        } catch (err) {
          item.onError?.(err as ProgressEvent<EventTarget>);
        }
      }

      uploadQueue.current = [];
    }, 100);
  };

  const onFinish = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values: FieldValues & Record<string, any>
  ) => {
    delete values.link;

    try {
      setCreateLoading(true);
      const res = await createAppointment({
        ...values,
        patient_id: Number(values.patient_id),
        doctor_id: Number(values.doctor_id),
        plan_id: values.plan_id != null ? Number(values.plan_id) : undefined,
        translator_plan_id:
          values.translator_plan_id != null
            ? Number(values.translator_plan_id)
            : undefined,
        date: dayjs(values.date).toISOString(),
        files,
        links
      });

      navigate(`/appointments/${res.id}`);
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <PageWrapper
      title={t('appointments.create')}
      color="white"
      extra={[
        <Button
          type="primary"
          onClick={form.submit}
          key="save"
          loading={createLoading}
        >
          {t('common.save')}
        </Button>
      ]}
    >
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Row style={{ width: '100%' }} gutter={[24, 0]}>
          <Col span={12}>
            <PatientSelect />
          </Col>
          <Col span={12}>
            <Form.Item
              shouldUpdate={(prevValues, curValues) => {
                setDoctorId(curValues.doctor_id);

                return prevValues.additional !== curValues.additional;
              }}
            >
              {() => {
                return (
                  <Form.Item
                    name="doctor_id"
                    label={t('appointments.doctor')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      size="large"
                      allowClear
                      showSearch
                      filterOption={(
                        input: string,
                        option?: { label: string; value: string }
                      ) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      loading={doctorsLoading}
                      options={doctors}
                      placeholder={t('appointments.doctor_placeholder')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          {!doctorID || (!!doctorID && !!doctorPlans?.length) ? (
            <>
              <Col span={12}>
                <Form.Item
                  name="plan_id"
                  label={t('appointments.plan')}
                  rules={[{ required: true }]}
                >
                  <Select
                    size="large"
                    loading={doctorProfileLoading}
                    placeholder={t('appointments.plan_placeholder')}
                    options={doctorPlans}
                    disabled={!doctorID || !doctorPlans?.length}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="translator_plan_id"
                  label={t('appointments.translator')}
                >
                  <Select
                    size="large"
                    loading={translatorPlansLoading}
                    placeholder={t('appointments.translator_placeholder')}
                    options={translatorPlans}
                    disabled={!translatorPlans?.length}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('appointments.status')}
                  name="paid"
                  rules={[{ required: true }]}
                >
                  <Select
                    size="large"
                    placeholder={t('appointments.status_placeholder')}
                    options={[
                      {
                        label: t('appointments.not_paid'),
                        value: false
                      },
                      {
                        label: t('appointments.paid'),
                        value: true
                      }
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="link"
                  label={t('appointments.links')}
                  rules={[
                    {
                      pattern: URL_VALIDATION,
                      message: t('appointments.link_is_not_valid')
                    }
                  ]}
                >
                  <Input
                    placeholder={t('appointments.link_here')}
                    size="large"
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        onAddLinkClick();
                      }
                    }}
                  />
                </Form.Item>
                <Button
                  type="primary"
                  htmlType="button"
                  onClick={onAddLinkClick}
                >
                  {t('appointments.add')}
                </Button>
                <ul style={{ marginTop: 16 }}>
                  {links.map((item, index) => (
                    <li key={`selected-link-${index}`}>
                      <a
                        href={validateLink(item)}
                        target="_blank"
                        rel="noreferrer"
                        style={{ wordBreak: 'break-all' }}
                      >
                        {item}
                      </a>
                    </li>
                  ))}
                </ul>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('appointments.select_date')}
                  name="date"
                  rules={[{ required: true }]}
                >
                  <DatePicker
                    suffixIcon={
                      <CalendarOutlined
                        style={{ fontSize: 14, color: '#09121F' }}
                      />
                    }
                    style={{ width: '100%' }}
                    showTime
                    size="large"
                    format={FULL_DATE_FORMAT}
                    showSecond={false}
                    placement={'bottomLeft'}
                    disabledDate={current => dayjs().diff(current, 'days') > 0}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="notes" label={t('appointments.notes')}>
                  <TextArea />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Space direction="vertical">
                  <Text>{t('appointments.documents')}</Text>
                  <Upload customRequest={onUploadFile} multiple>
                    <Button>{t('appointments.upload_file')}</Button>
                  </Upload>
                </Space>
              </Col>
            </>
          ) : (
            <Col span={24}>
              <Result
                status="error"
                title={t('appointments.plan_empty')}
                subTitle={
                  <Text>
                    {t('appointments.create_plan')}
                    {'  '}
                    <Link
                      onClick={() =>
                        navigate(
                          `/access/doctors/${doctorID}/personal-information`
                        )
                      }
                    >
                      {t('appointments.click')}
                    </Link>
                  </Text>
                }
              />
            </Col>
          )}
        </Row>
      </Form>
    </PageWrapper>
  );
};

export default CreateAppointment;
